/**
 * 格式化工具函数
 * 
 * 通用格式化函数，可在前端各处使用
 * 
 * @author YSFL Team
 * @date 2025-10-01
 */

/**
 * 格式化日期
 * @param date 日期对象或字符串
 * @param format 格式化模板，默认 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串
 */
export function formatDate(date: Date | string, format: string = 'YYYY-MM-DD'): string {
  const d = new Date(date)
  
  if (isNaN(d.getTime())) {
    return ''
  }
  
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hour = String(d.getHours()).padStart(2, '0')
  const minute = String(d.getMinutes()).padStart(2, '0')
  const second = String(d.getSeconds()).padStart(2, '0')
  
  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hour)
    .replace('mm', minute)
    .replace('ss', second)
}

/**
 * 格式化日期时间
 * @param date 日期对象或字符串
 * @returns 格式化后的日期时间字符串
 */
export function formatDateTime(date: Date | string): string {
  return formatDate(date, 'YYYY-MM-DD HH:mm:ss')
}

/**
 * 格式化金额
 * @param amount 金额数值
 * @param decimals 小数位数，默认2位
 * @param currency 货币符号，默认'¥'
 * @returns 格式化后的金额字符串
 */
export function formatMoney(amount: number, decimals: number = 2, currency: string = '¥'): string {
  if (isNaN(amount)) {
    return `${currency}0.00`
  }
  
  const fixed = amount.toFixed(decimals)
  const parts = fixed.split('.')
  const intPart = (parts[0] ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  
  return decimals > 0 
    ? `${currency}${intPart}.${parts[1] ?? '00'}`
    : `${currency}${intPart}`
}

/**
 * 格式化百分比
 * @param value 数值（0-1之间）
 * @param decimals 小数位数，默认2位
 * @returns 格式化后的百分比字符串
 */
export function formatPercent(value: number, decimals: number = 2): string {
  if (isNaN(value)) {
    return '0%'
  }
  
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

/**
 * 格式化手机号（中间4位隐藏）
 * @param phone 手机号
 * @returns 格式化后的手机号
 */
export function formatPhone(phone: string): string {
  if (!phone || phone.length !== 11) {
    return phone
  }
  
  return `${phone.slice(0, 3)}****${phone.slice(7)}`
}

/**
 * 格式化身份证号（中间部分隐藏）
 * @param idCard 身份证号
 * @returns 格式化后的身份证号
 */
export function formatIdCard(idCard: string): string {
  if (!idCard || idCard.length < 15) {
    return idCard
  }
  
  return `${idCard.slice(0, 6)}********${idCard.slice(-4)}`
}

/**
 * 千位分隔符
 * @param num 数字
 * @returns 格式化后的数字字符串
 */
export function formatNumber(num: number): string {
  if (isNaN(num)) {
    return '0'
  }

  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * 数量显示格式选项
 */
export interface FormatQuantityOptions {
  /**
   * 显示小数位上限（超上限四舍五入进位）。
   * 默认 6，等于当前库内 decimal 列最大 scale，保证常规数量显示不引入舍入误差；
   * 统计卡、报表等聚合展示场景可显式传 2 收紧口径
   */
  maxDecimals?: number
  /** 是否千分位分组，默认开启 */
  thousands?: boolean
  /** 空/非法值占位符，默认 '0'；报表场景可传 '-' */
  placeholder?: string
}

/**
 * 数量显示格式唯一入口（全端数量字段显示必须走本函数）
 *
 * 显示规则（真相源：根仓 docs/01-governance/数量显示格式规范.md）：
 * - 整数不带小数点（10 不显示 10.00）
 * - 小数按实际精度展示并去除尾随 0（10.500 显示 10.5，不铺无意义尾零）
 * - 默认不因显示四舍五入截断有效精度（上限=库内 decimal 最大 scale）
 * - 千分位默认开启
 *
 * 后端 decimal:N cast 以固定小数字符串形式返回（"10.000000"，防精度丢失），
 * 部分 Resource 已做 (float) 归一，入参因此兼容 number | string。
 *
 * @param value 数量值（number/string/空值）
 * @param options 显示选项
 * @returns 格式化后的数量字符串
 * @example
 * formatQuantity(10)            // '10'
 * formatQuantity('10.000000')   // '10'
 * formatQuantity(10.5)          // '10.5'
 * formatQuantity('1234.5678')   // '1,234.5678'
 * formatQuantity(0)             // '0'
 * formatQuantity(null)          // '0'
 * formatQuantity(12.345, { maxDecimals: 2 }) // '12.35'（显式收口场景）
 */
export function formatQuantity(
  value: number | string | null | undefined,
  options: FormatQuantityOptions = {},
): string {
  const { maxDecimals = 6, thousands = true, placeholder = '0' } = options

  if (value === null || value === undefined || value === '') {
    return placeholder
  }

  const num = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(num)) {
    return placeholder
  }

  const cappedDecimals = Math.min(Math.max(Math.trunc(maxDecimals), 0), 100)
  const [rawIntPart = '', rawDecPart = ''] = num
    .toFixed(cappedDecimals)
    .split('.')
  const trimmedDecPart = rawDecPart.replace(/0+$/, '')
  const groupedIntPart = thousands
    ? rawIntPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : rawIntPart

  return trimmedDecPart ? `${groupedIntPart}.${trimmedDecPart}` : groupedIntPart
}

/**
 * 格式化时长（秒转为 HH:mm:ss）
 * @param seconds 秒数
 * @returns 格式化后的时长字符串
 */
export function formatDuration(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) {
    return '00:00:00'
  }
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  return [hours, minutes, secs]
    .map(v => String(v).padStart(2, '0'))
    .join(':')
}

/**
 * 格式化相对时间（多久之前/之后）
 * @param date 日期对象或字符串
 * @returns 相对时间字符串（如：刚刚、5分钟前、3小时前、2天前）
 * @example
 * formatRelativeTime(new Date()) // '刚刚'
 * formatRelativeTime(Date.now() - 60000) // '1分钟前'
 */
export function formatRelativeTime(date: Date | string | number): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return ''
  }
  
  const now = Date.now()
  const diffMs = now - d.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  const diffMonth = Math.floor(diffDay / 30)
  const diffYear = Math.floor(diffDay / 365)
  
  if (diffSec < 60) {
    return '刚刚'
  } else if (diffMin < 60) {
    return `${diffMin}分钟前`
  } else if (diffHour < 24) {
    return `${diffHour}小时前`
  } else if (diffDay < 30) {
    return `${diffDay}天前`
  } else if (diffMonth < 12) {
    return `${diffMonth}个月前`
  } else {
    return `${diffYear}年前`
  }
}

/**
 * 计算日期差异（天数）
 * @param startDate 开始日期
 * @param endDate 结束日期，默认为当前日期
 * @returns 天数差异
 * @example
 * dateDiff('2025-01-01', '2025-01-10') // 9
 * dateDiff('2025-01-01') // 距离今天的天数
 */
export function dateDiff(startDate: Date | string, endDate?: Date | string): number {
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : new Date()
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0
  }
  
  const diffMs = end.getTime() - start.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * 日期加减运算
 * @param date 基准日期
 * @param days 天数（正数为加，负数为减）
 * @param format 返回格式，默认 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串
 * @example
 * dateAdd('2025-01-01', 7) // '2025-01-08'
 * dateAdd('2025-01-01', -7) // '2024-12-25'
 */
export function dateAdd(date: Date | string, days: number, format: string = 'YYYY-MM-DD'): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return ''
  }
  
  d.setDate(d.getDate() + days)
  return formatDate(d, format)
}

/**
 * 获取日期所在周的开始和结束日期
 * @param date 日期，默认为当前日期
 * @param weekStartsOn 一周从星期几开始（0-6，0为周日），默认为1（周一）
 * @returns { start, end } 周开始和结束日期
 * @example
 * getWeekRange('2025-01-15') // { start: '2025-01-13', end: '2025-01-19' }
 */
export function getWeekRange(date?: Date | string, weekStartsOn: number = 1): { start: string; end: string } {
  const d = date ? new Date(date) : new Date()
  if (isNaN(d.getTime())) {
    return { start: '', end: '' }
  }
  
  const day = d.getDay()
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn
  
  const start = new Date(d)
  start.setDate(d.getDate() - diff)
  
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  
  return {
    start: formatDate(start),
    end: formatDate(end),
  }
}

/**
 * 获取日期所在月的开始和结束日期
 * @param date 日期，默认为当前日期
 * @returns { start, end } 月开始和结束日期
 * @example
 * getMonthRange('2025-01-15') // { start: '2025-01-01', end: '2025-01-31' }
 */
export function getMonthRange(date?: Date | string): { start: string; end: string } {
  const d = date ? new Date(date) : new Date()
  if (isNaN(d.getTime())) {
    return { start: '', end: '' }
  }
  
  const year = d.getFullYear()
  const month = d.getMonth()
  
  const start = new Date(year, month, 1)
  const end = new Date(year, month + 1, 0)
  
  return {
    start: formatDate(start),
    end: formatDate(end),
  }
}

/**
 * 判断是否为工作日（周一到周五）
 * @param date 日期
 * @returns true为工作日，false为周末
 * @example
 * isWeekday('2025-01-13') // true (周一)
 * isWeekday('2025-01-18') // false (周六)
 */
export function isWeekday(date: Date | string): boolean {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return false
  }
  
  const day = d.getDay()
  return day >= 1 && day <= 5
}

/**
 * 计算两个日期之间的工作日天数
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @returns 工作日天数
 * @example
 * getWorkdays('2025-01-13', '2025-01-19') // 5 (周一到周五)
 */
export function getWorkdays(startDate: Date | string, endDate: Date | string): number {
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0
  }
  
  let count = 0
  const current = new Date(start)
  
  while (current <= end) {
    if (isWeekday(current)) {
      count++
    }
    current.setDate(current.getDate() + 1)
  }
  
  return count
}

/**
 * 格式化为ISO 8601日期时间字符串
 * 
 * @deprecated 已废弃。项目统一使用 'YYYY-MM-DD HH:mm:ss' 格式，请使用 formatDateTime() 代替。
 * 后端API响应时间格式已统一为 'Y-m-d H:i:s'，不再使用ISO8601格式。
 * 此函数将在未来版本中移除。
 * 
 * @param date 日期对象或字符串
 * @returns ISO 8601格式的字符串（YYYY-MM-DDTHH:mm:ss.SSSZ）
 * @example
 * // 不推荐使用
 * toISO8601(new Date()) // '2025-01-15T10:30:00.000Z'
 * // 推荐使用
 * formatDateTime(new Date()) // '2025-01-15 10:30:00'
 */
export function toISO8601(date: Date | string): string {
  console.warn('[DEPRECATED] toISO8601() 已废弃，请使用 formatDateTime() 代替')
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return ''
  }
  
  return d.toISOString()
}

